import Head from "next/head";
import { useEffect, useMemo, useState } from "react";
import {
  fetchMenu,
  getCountryName,
  getDisplayAddress,
  getMapUrl,
} from "@/services/menuService";
import { capitalizeText, getImageUrl, showAds } from "@/utilities/menu";
import Footer from "@/components/layout/Footer";
import { Menu, Review } from "@/types/types";
import AdSenseAd from "@/components/google-adSense/AdSenseAd";
import AdsComponent from "@/components/ads-component/AdsComponent";
import RatingStars from "@/components/rating/RatingStars";
import MapComponent from "@/components/MapComponent";
import { fetchReviews } from "@/services/reviewsService";
import ReservationForm from "@/components/reservation/ReservationForm";
import ReviewItem from "@/components/rating/ReviewItem";
import FacebookIcon from "@/components/icons/FacebookIcon";
import WhatsappIcon from "@/components/icons/WhatsappIcon";
import MailIcon from "@/components/icons/MailIcon";
import MessengerIcon from "@/components/icons/MessengerIcon";
import SMSIcon from "@/components/icons/SMSIcon";
import CuisineTypes from "@/components/cuisine-types/CuisineTypes";

export const getServerSideProps = async (
  context: any
): Promise<
  { props: HomeProps } | { notFound: boolean } | { redirect: any }
> => {
  const { menu: siteName } = context.query;

  if (!siteName) {
    return { notFound: true };
  }

  let menu: Menu | undefined;

  if (siteName) {
    const data = await fetchMenu(siteName);
    if (data) {
      menu = data as Menu;
      const formattedSiteName = capitalizeText(
        menu?.siteName?.replace(/-/g, " ")
      );
      const industry = capitalizeText(
        menu?.companyInfo?.storeIndustry || "restaurant"
      );
      menu.title = `${formattedSiteName} ${
        getCountryName(menu.companyInfo?.country) || ""
      } ${industry} ${menu.companyInfo?.city || ""}`;

      menu.description = `Welcome to the official restaurant of ${formattedSiteName} in ${
        menu.companyInfo?.city || ""
      }, ${getCountryName(
        menu.companyInfo?.country
      )} -  ${industry} Menu, book online - Photos & Phone Number`;
    } else {
      return { notFound: true };
    }
  } else {
    return { notFound: true };
  }

  return {
    props: {
      menu,
    },
  };
};

interface HomeProps {
  menu: Menu;
}

const MenuRestaurantPage: React.FC<HomeProps> = ({ menu }) => {
  const menuName = useMemo(
    () => capitalizeText(menu?.siteName?.replace(/-/g, " ")),
    [menu]
  );
  const menuImage = useMemo(() => getImageUrl(menu?.imageMenu || ""), [menu]);

  const [reviews, setReviews] = useState<Partial<Review>[]>([]);
  useEffect(() => {
    fetchReviews(menu?.siteName).then((reviews) => {
      if (reviews) {
        setReviews(reviews);
      }
    });
  }, [menu]);

  const averageRating =
    reviews.reduce((acc, review) => acc + (review.rating || 0), 0) /
    reviews.length;

  const [viewPhone, setViewPhone] = useState<boolean>(false);

  const shouldShowAds = showAds(menu);

  const copyMenuToClipboard = (
    menu: Menu,
    reviews: Partial<Review>[]
  ): void => {
    // Destructure to remove unwanted properties and keep the rest of the menu
    const {
      logo,
      logoHeight,
      logoWidth,
      imageMenu,
      subscribed,
      orderOnline,
      categoriesOrder,
      languages,
      ...restOfMenu
    } = menu;

    // Map over products to keep only title (en), description (en), and price
    const products = menu.products.map(({ title, description, price }) => ({
      title: title,
      description: description,
      price,
    }));
    const categories = menu.categories.map(({ title }) => ({
      title: title,
    }));
    // Create a new object with the cleaned menu and products
    const cleanedMenu = {
      ...restOfMenu,
      products,
      categories,
      reviews,
    };

    // Convert the cleaned menu to a JSON string
    const jsonString = JSON.stringify(cleanedMenu, null, 4);

    // Use the Clipboard API to copy the string
    navigator.clipboard
      .writeText(jsonString)
      .then(() => {
        console.log("Menu copied to clipboard successfully!");
      })
      .catch((err) => {
        console.error("Failed to copy menu to clipboard", err);
      });
  };

  return (
    <>
      <Head>
        <title>{menu.title}</title>
        <meta name="description" content={menu.description} />
      </Head>
      <div
        className="bg-cover bg-center min-h-[20vh] flex items-center justify-center md:py-20"
        style={{
          backgroundImage: `url('https://dopweb-repository.s3.us-west-2.amazonaws.com/qrmenu/mydop-images/img5d5db67a-221c-4e9d-87fb-f2d56bd9a51e.webp')`,
        }}
      >
        <div className="container mx-auto text-center">
          <div className="flex flex-wrap justify-center">
            <div className="w-full px-4">
              <h1 className="text-4xl font-bold text-black mb-4">{menuName}</h1>
            </div>
          </div>
        </div>
      </div>
      <div className="container mx-auto p-4">
        <div className="flex flex-wrap">
          {/* Left side with image and map */}
          <div className="w-full md:w-8/12 p-2">
            {menu.companyInfo && (
              <div className="mb-3">
                <div className="text-gray-400 text-[14px]">
                  <a href={`${process.env.NEXT_PUBLIC_URL}/restaurants/`}>
                    Restaurant
                  </a>
                  {" / "}
                  <a
                    href={`${
                      process.env.NEXT_PUBLIC_URL
                    }/restaurants/${getCountryName(menu.companyInfo?.country)
                      ?.replaceAll(" ", "-")
                      .toLocaleLowerCase()}/`}
                  >
                    {getCountryName(menu.companyInfo?.country)}
                  </a>
                  {" / "}
                  <a
                    href={`${
                      process.env.NEXT_PUBLIC_URL
                    }/restaurants/${getCountryName(menu.companyInfo?.country)
                      ?.replaceAll(" ", "-")
                      .toLocaleLowerCase()}/${menu.companyInfo?.city
                      ?.replaceAll(" ", "-")
                      .toLocaleLowerCase()}/`}
                  >
                    {menu.companyInfo?.city}
                  </a>
                </div>
              </div>
            )}

            {reviews.length ? (
              <div className="mb-4 min-h-[20px]">
                <RatingStars
                  currentColor="#03BC6E"
                  rating={averageRating}
                  onRatingChange={() => {}}
                />
              </div>
            ) : null}

            <div className="mb-1">
              <h2 className="text-2xl font-bold mb-1">{menuName}</h2>

              <CuisineTypes types={menu?.companyInfo?.cuisineType || []} />
              {menu.companyInfo && (
                <a
                  className="flex items-center"
                  href={getMapUrl(menu.companyInfo)}
                  target="_blank"
                  rel="noopener noreferrer"
                  title={menu.companyInfo?.address}
                >
                  <svg
                    width="20"
                    height="20"
                    viewBox="0 0 20 20"
                    fill="none"
                    xmlns="http://www.w3.org/2000/svg"
                  >
                    <path
                      d="M10 1.66667C8.24338 1.66658 6.55772 2.3598 5.30937 3.59566C4.06103 4.83152 3.3509 6.51014 3.33334 8.26667C3.33334 12.8333 9.20834 17.9167 9.45834 18.1333C9.60928 18.2624 9.80137 18.3334 10 18.3334C10.1986 18.3334 10.3907 18.2624 10.5417 18.1333C10.8333 17.9167 16.6667 12.8333 16.6667 8.26667C16.6491 6.51014 15.939 4.83152 14.6906 3.59566C13.4423 2.3598 11.7566 1.66658 10 1.66667ZM10 16.375C8.60834 15.05 5 11.375 5 8.26667C5 6.94059 5.52679 5.66882 6.46447 4.73114C7.40215 3.79346 8.67392 3.26667 10 3.26667C11.3261 3.26667 12.5979 3.79346 13.5355 4.73114C14.4732 5.66882 15 6.94059 15 8.26667C15 11.35 11.3917 15.05 10 16.375Z"
                      fill="#03BC6E"
                    />
                    <path
                      d="M10 5C9.42313 5 8.85923 5.17106 8.37958 5.49155C7.89994 5.81203 7.5261 6.26756 7.30535 6.80051C7.08459 7.33346 7.02683 7.9199 7.13937 8.48568C7.25191 9.05146 7.5297 9.57116 7.9376 9.97906C8.3455 10.387 8.8652 10.6648 9.43098 10.7773C9.99676 10.8898 10.5832 10.8321 11.1162 10.6113C11.6491 10.3906 12.1046 10.0167 12.4251 9.53708C12.7456 9.05744 12.9167 8.49353 12.9167 7.91667C12.9167 7.14312 12.6094 6.40125 12.0624 5.85427C11.5154 5.30729 10.7735 5 10 5ZM10 9.16667C9.75277 9.16667 9.5111 9.09336 9.30553 8.956C9.09997 8.81865 8.93976 8.62343 8.84515 8.39502C8.75054 8.16661 8.72578 7.91528 8.77401 7.6728C8.82225 7.43033 8.9413 7.2076 9.11611 7.03278C9.29093 6.85797 9.51366 6.73892 9.75613 6.69068C9.99861 6.64245 10.2499 6.66721 10.4784 6.76182C10.7068 6.85643 10.902 7.01664 11.0393 7.2222C11.1767 7.42777 11.25 7.66944 11.25 7.91667C11.25 8.24819 11.1183 8.56613 10.8839 8.80055C10.6495 9.03497 10.3315 9.16667 10 9.16667Z"
                      fill="#03BC6E"
                    />
                  </svg>
                  <p className="text-gray-600 text-xs">
                    {getDisplayAddress(menu.companyInfo)}
                  </p>
                </a>
              )}
            </div>

            <div className="flex justify-start mb-4 flex-wrap">
              {menu.companyInfo?.phoneNumber ? (
                <button
                  className="mt-3 mr-4 text-left text-white bg-[#03BC6E] hover:bg-green-500 text-sm h-[58px] px-4 border border-green-500 hover:border-transparent pointer min-w-[184px]"
                  onClick={() => {
                    if (viewPhone) {
                      window.location.href = `tel:${menu.companyInfo.phoneNumber}`;
                    } else {
                      setViewPhone(true);
                    }
                  }}
                >
                  <svg
                    width="27"
                    height="27"
                    viewBox="0 0 31 31"
                    fill="none"
                    xmlns="http://www.w3.org/2000/svg"
                    className="inline-block mr-4"
                  >
                    <path
                      d="M26.733 19.3743L21.2444 16.9149L21.2293 16.9079C20.9443 16.786 20.6335 16.7371 20.3249 16.7656C20.0163 16.7941 19.7197 16.8991 19.4619 17.071C19.4315 17.0911 19.4023 17.1128 19.3745 17.1363L16.5387 19.5538C14.7422 18.6811 12.8874 16.8403 12.0148 15.0671L14.4358 12.1882C14.4591 12.1591 14.4812 12.13 14.5022 12.0985C14.6705 11.8414 14.7726 11.5467 14.7994 11.2406C14.8262 10.9344 14.777 10.6264 14.656 10.344V10.33L12.1896 4.83205C12.0297 4.46303 11.7547 4.15563 11.4057 3.95574C11.0567 3.75584 10.6524 3.67417 10.2532 3.72291C8.67455 3.93065 7.22545 4.70595 6.17661 5.90402C5.12777 7.10208 4.55091 8.64097 4.55376 10.2333C4.55376 19.4839 12.0801 27.0101 21.3306 27.0101C22.9229 27.013 24.4618 26.4361 25.6599 25.3873C26.858 24.3385 27.6333 22.8894 27.841 21.3107C27.8898 20.9116 27.8083 20.5074 27.6087 20.1585C27.409 19.8095 27.1018 19.5344 26.733 19.3743ZM21.3306 25.1461C17.3768 25.1417 13.5862 23.5692 10.7905 20.7734C7.99472 17.9777 6.42217 14.1871 6.41786 10.2333C6.41347 9.09558 6.82336 7.99519 7.57096 7.1376C8.31856 6.28001 9.35277 5.72387 10.4804 5.57303C10.48 5.57768 10.48 5.58236 10.4804 5.58701L12.9271 11.0628L10.5189 13.9452C10.4944 13.9733 10.4722 14.0033 10.4525 14.0349C10.2771 14.3039 10.1743 14.6137 10.1539 14.9341C10.1335 15.2546 10.1962 15.5749 10.336 15.864C11.3915 18.0229 13.5667 20.1817 15.7488 21.2361C16.0401 21.3746 16.3622 21.4351 16.6838 21.4118C17.0055 21.3886 17.3156 21.2823 17.5838 21.1033C17.6137 21.0831 17.6425 21.0614 17.67 21.038L20.5023 18.6217L25.9781 21.0742H25.9909C25.8419 22.2034 25.2866 23.2397 24.4288 23.9892C23.5711 24.7386 22.4697 25.1499 21.3306 25.1461Z"
                      fill="currentColor"
                    />
                  </svg>
                  {!viewPhone
                    ? "View phone number"
                    : menu.companyInfo.phoneNumber}
                </button>
              ) : (
                <></>
              )}
              <div className="flex md:ml-auto mt-3 md:w-auto w-full">
                <a
                  className="flex items-center text-gray-500 bg-transparent hover:bg-gray-700 hover:text-white text-sm h-[58px] px-4 border border-gray-500 hover:border-transparent pointer md:w-auto w-full"
                  href={getMapUrl(menu.companyInfo)}
                  target="_blank"
                  rel="noopener noreferrer"
                  title={menu.companyInfo?.address}
                >
                  <svg
                    width="27"
                    height="27"
                    viewBox="0 0 27 27"
                    fill="none"
                    xmlns="http://www.w3.org/2000/svg"
                    className="inline-block mr-3"
                  >
                    <path
                      d="M13.5 15.75C15.9818 15.75 18 13.7318 18 11.25C18 8.76825 15.9818 6.75 13.5 6.75C11.0182 6.75 9 8.76825 9 11.25C9 13.7318 11.0182 15.75 13.5 15.75ZM13.5 9C14.7409 9 15.75 10.0091 15.75 11.25C15.75 12.4909 14.7409 13.5 13.5 13.5C12.2591 13.5 11.25 12.4909 11.25 11.25C11.25 10.0091 12.2591 9 13.5 9Z"
                      fill="currentColor"
                    />
                    <path
                      d="M12.8475 24.5407C13.0379 24.6767 13.266 24.7498 13.5 24.7498C13.734 24.7498 13.9621 24.6767 14.1525 24.5407C14.4945 24.2989 22.5326 18.495 22.5 11.25C22.5 6.28763 18.4624 2.25 13.5 2.25C8.53763 2.25 4.50001 6.28762 4.50001 11.2444C4.46738 18.495 12.5055 24.2989 12.8475 24.5407ZM13.5 4.5C17.2226 4.5 20.25 7.52738 20.25 11.2556C20.2736 16.2484 15.3135 20.7315 13.5 22.2019C11.6876 20.7304 6.72638 16.2461 6.75001 11.25C6.75001 7.52738 9.77738 4.5 13.5 4.5Z"
                      fill="currentColor"
                    />
                  </svg>
                  Location
                </a>
                <a
                  className="flex items-center text-[#03BC6E] bg-transparent hover:bg-green-500 hover:text-white text-sm h-[58px] px-4 border border-green-500 hover:border-transparent ml-4 pointer md:w-auto w-full"
                  href={`${process.env.NEXT_PUBLIC_URL}/menus/${menu.siteName}/`}
                >
                  <svg
                    width="35"
                    height="35"
                    viewBox="0 0 31 35"
                    fill="none"
                    xmlns="http://www.w3.org/2000/svg"
                    className="inline-block mr-4"
                  >
                    <path
                      d="M17.717 13.9883V12.6321C18.4018 12.2354 19.1359 11.9379 19.9194 11.7396C20.7028 11.5412 21.5062 11.4421 22.3297 11.4421C22.8009 11.4421 23.2531 11.4766 23.686 11.5456C24.1199 11.6146 24.5673 11.7128 25.0284 11.8402V13.1644C24.5835 13.002 24.1497 12.8892 23.7269 12.826C23.3041 12.7638 22.8384 12.7327 22.3297 12.7327C21.5045 12.7327 20.6998 12.8387 19.9155 13.0506C19.1304 13.2626 18.3975 13.5751 17.717 13.9883ZM17.717 21.9537V20.5392C18.3695 20.1435 19.0993 19.8469 19.9066 19.6496C20.7139 19.4503 21.5216 19.3506 22.3297 19.3506C22.8009 19.3506 23.2531 19.3851 23.686 19.4542C24.1199 19.5242 24.5673 19.6223 25.0284 19.7487V21.0729C24.5835 20.9105 24.1497 20.7978 23.7269 20.7346C23.3041 20.6723 22.8384 20.6412 22.3297 20.6412C21.5045 20.6412 20.6998 20.755 19.9155 20.9825C19.1304 21.211 18.3975 21.5347 17.717 21.9537ZM17.717 17.9987V16.5856C18.4018 16.1889 19.1359 15.8919 19.9194 15.6946C20.702 15.4962 21.5054 15.3971 22.3297 15.3971C22.8009 15.3971 23.2531 15.4311 23.686 15.4992C24.1199 15.5692 24.5673 15.6678 25.0284 15.7952V17.1179C24.5835 16.9555 24.1497 16.8433 23.7269 16.781C23.3041 16.7178 22.8384 16.6862 22.3297 16.6862C21.5045 16.6862 20.6998 16.8019 19.9155 17.0333C19.1304 17.2637 18.3975 17.5855 17.717 17.9987ZM8.29389 23.3887C9.40824 23.3887 10.4916 23.5351 11.5438 23.8277C12.5969 24.1203 13.6402 24.5967 14.6738 25.2569V10.9492C13.7525 10.1976 12.7373 9.63374 11.628 9.25749C10.5196 8.88221 9.40824 8.69457 8.29389 8.69457C7.5283 8.69457 6.85798 8.74658 6.28294 8.85061C5.7079 8.95561 5.06991 9.15006 4.36897 9.43394C4.17162 9.50881 4.03211 9.61624 3.95045 9.75624C3.86878 9.89721 3.82795 10.0518 3.82795 10.22V23.3669C3.82795 23.6294 3.90962 23.8209 4.07294 23.9414C4.23712 24.063 4.41703 24.0776 4.61268 23.9852C5.07884 23.8053 5.61773 23.661 6.22935 23.5521C6.84012 23.4432 7.5283 23.3887 8.29389 23.3887ZM15.9498 25.2569C16.9833 24.5967 18.0266 24.1203 19.0798 23.8277C20.132 23.536 21.2153 23.3902 22.3297 23.3902C23.0953 23.3902 23.7834 23.4442 24.3942 23.5521C25.0058 23.661 25.5447 23.8048 26.0109 23.9837C26.2065 24.0771 26.3864 24.063 26.5506 23.9414C26.714 23.8199 26.7956 23.6284 26.7956 23.3669V10.22C26.7956 10.0518 26.7548 9.90207 26.6731 9.77082C26.5915 9.63957 26.4524 9.52776 26.2559 9.4354C25.5541 9.15054 24.9157 8.9561 24.3406 8.85207C23.7656 8.74707 23.0953 8.69457 22.3297 8.69457C21.2153 8.69457 20.1039 8.88221 18.9955 9.25749C17.8863 9.63374 16.871 10.1976 15.9498 10.9492V25.2569ZM15.3118 27.3714C14.274 26.5606 13.1596 25.9374 11.9687 25.5019C10.7778 25.0653 9.55286 24.8471 8.29389 24.8471C7.63038 24.8471 6.97792 24.9108 6.33653 25.0381C5.69599 25.1655 5.07246 25.3696 4.46594 25.6506C4.00319 25.8616 3.5685 25.7979 3.16189 25.4596C2.75528 25.1212 2.55197 24.6536 2.55197 24.0567V9.96186C2.55197 9.60117 2.63491 9.26624 2.80079 8.95707C2.96667 8.64888 3.19932 8.43401 3.49875 8.31249C4.24818 7.93526 5.0278 7.66158 5.83762 7.49145C6.64745 7.32131 7.4662 7.23624 8.29389 7.23624C9.54435 7.23624 10.7633 7.44186 11.9508 7.85311C13.1392 8.26436 14.2595 8.86228 15.3118 9.64686C16.364 8.86228 17.4844 8.26436 18.6727 7.85311C19.8611 7.44186 21.0801 7.23624 22.3297 7.23624C23.1574 7.23624 23.9761 7.32131 24.7859 7.49145C25.5958 7.66158 26.3754 7.93526 27.1248 8.31249C27.4242 8.43401 27.6569 8.64888 27.8228 8.95707C27.9887 9.26624 28.0716 9.60117 28.0716 9.96186V24.0567C28.0716 24.6536 27.8521 25.1115 27.4132 25.4304C26.9734 25.7512 26.5055 25.8057 26.0096 25.5937C25.4193 25.3312 24.8161 25.1417 24.2003 25.025C23.5844 24.9064 22.9609 24.8471 22.3297 24.8471C21.0707 24.8471 19.8458 25.0653 18.6549 25.5019C17.4639 25.9374 16.3496 26.5606 15.3118 27.3714Z"
                      fill="currentColor"
                    />
                  </svg>
                  View Menu
                </a>
              </div>
            </div>
            <img
              className="w-full h-auto object-cover"
              src={menuImage}
              alt={`Menu image of ${menu.title}`}
            />
            {menu?.companyInfo?.description ? (
              <>
                <h3 className="leading-5 mb-1 mt-5">Restaurant description:</h3>
                <p className="text-[13px] font-light leading-5 mb-1 mt-1">
                  {menu?.companyInfo?.description}
                </p>
              </>
            ) : null}
            {/* Map */}
            <div className="mt-4">
              <p
                className="mb-2"
                onClick={(e) => {
                  // Check if Ctrl (or Cmd on macOS) is pressed during the click
                  /* if (e.ctrlKey || e.metaKey) {
                    copyMenuToClipboard(menu, reviews);
                  } */
                }}
              >
                Going to the{" "}
                {menu?.companyInfo?.storeIndustry?.replaceAll("-", " ") ||
                  "restaurant"}
              </p>
              <MapComponent
                businessName={menuName}
                address={getDisplayAddress(menu.companyInfo)}
              />
            </div>
          </div>

          {/* Right side with form */}
          <div className="w-full md:w-4/12 p-2 md:pl-5 h-full ">
            <div className="flex flex-col mx-auto py-16 md:px-6 px-3 bg-[#F6F6F6] shadow-md h-full -mx-6 md:mx-0 relative">
              <h2 className="text-2xl font-bold mb-2 text-black text-center">
                BOOK A TABLE
              </h2>
              <p className="text-black text-sm mb-4 text-center max-w-[310px] mx-auto">
                {menuName} will respond very quickly
                <br />
                Reservation to be confirmed after validation by the restaurant.
              </p>
              <ReservationForm siteName={menu.siteName} />
            </div>
          </div>
        </div>
      </div>
      <div className="container mx-auto p-4">
        <div className="flex flex-wrap">
          {/* Left side with image and map */}
          <div className="w-full md:w-8/12 p-2">
            <div className="border border-[#DFDFDF] p-2 text-sm max-w-[440px] mb-8">
              <span className="relative bg-[#FF0000] font-semibold text-white px-2 py-1 -ml-[15px] rounded-ml error-box">
                An error has occurred?
              </span>
              <a
                className="py-1 ml-6 underline"
                href={`mailto:hello@qrmenu.com?subject=${encodeURIComponent(
                  `Bug report for ${menu.siteName}`
                )}&body=${encodeURIComponent(
                  "Please describe the issue in detail."
                )}`}
              >
                Report a bug on this listing
              </a>{" "}
            </div>
            <div className="flex flex-col items-center mx-auto pt-8 pb-16 px-6 border border-[#DFDFDF] shadow-md rounded">
              {!reviews.length ? (
                <p className="font-bold text-[18px] text-center mt-3">
                  No reviews yet. Be the first to give your opinion!
                </p>
              ) : (
                <>
                  {reviews.map((review, index) => (
                    <ReviewItem key={index} review={review} />
                  ))}
                </>
              )}

              <a
                className="px-6 py-3 text-white text-center font-bold bg-[#03BC6E] mt-4 rounded-full w-[187px]"
                href={`/menus/reviews/?menu=${menu.siteName}`}
              >
                Write a review
              </a>
            </div>
          </div>

          {/* Right side with form */}
          <div className="w-full md:w-4/12 py-2 md:pl-5">
            <h5 className="font-bold text-[18px] text-center mb-3">
              Share this page with a friend
            </h5>
            <div className="flex gap-2 justify-center">
              <a
                className="px-3 py-2 bg-[#444444] text-white rounded-md"
                href={`sms:?&body=Check out this amazing restaurant here: ${process.env.NEXT_PUBLIC_URL}/restaurant/${menu.siteName}/`}
                target="_blank"
              >
                <SMSIcon />
              </a>
              <a
                className="px-3 py-2 bg-[#279ef0] rounded-md"
                href={`https://www.facebook.com/sharer/sharer.php?u=${process.env.NEXT_PUBLIC_URL}/restaurant/${menu.siteName}/`}
                target="_blank"
              >
                <FacebookIcon />
              </a>
              <a
                className="px-3 py-2 bg-[#2278ff] rounded-md"
                href={`fb-messenger://share/?link=${encodeURIComponent(
                  `${process.env.NEXT_PUBLIC_URL}/restaurant/${menu.siteName}/`
                )}`}
                target="_blank"
              >
                <MessengerIcon />
              </a>
              <a
                className="px-3 py-2 bg-[#48d366] rounded-md text-white"
                href={`https://api.whatsapp.com/send?text=Check out this amazing restaurant here: ${process.env.NEXT_PUBLIC_URL}/restaurant/${menu.siteName}/`}
                target="_blank"
              >
                <WhatsappIcon />
              </a>
              <a
                className="px-3 py-2 bg-[#f04a44] rounded-md text-white"
                href={`mailto:?subject=Check out this restaurant&body=Check out this amazing restaurant here: ${process.env.NEXT_PUBLIC_URL}/restaurant/${menu.siteName}/`}
                target="_blank"
              >
                <MailIcon />
              </a>
            </div>
          </div>
        </div>
      </div>
      <div className="flex flex-col relative">
        {/* Aff ads */}
        {shouldShowAds ? (
          <AdsComponent
            menuName={menu.siteName}
            country={menu.companyInfo?.country}
          />
        ) : (
          <></>
        )}

        {/* Google ads */}
        {shouldShowAds ? (
          <div className="mt-5 mb-[5%] mx-[5%] text-right">
            <AdSenseAd
              adClient="ca-pub-9101961507466056"
              adSlot="9338909219"
              style={{
                height: "300px",
              }} // Square dimensions
              format="auto"
              fullWidthResponsive="true"
            />
          </div>
        ) : (
          <></>
        )}

        {/* Footer Section */}
        <Footer siteName={menu.siteName} loadAds={shouldShowAds} />
      </div>
    </>
  );
};
export default MenuRestaurantPage;
